import { autocomplete } from '@algolia/autocomplete-js'
import { useTranslation } from 'react-i18next'
import '@algolia/autocomplete-theme-classic'
import React, { createElement, Fragment, useEffect, useRef } from 'react'
import { render } from 'react-dom'

// Wraps every function prop (and each `navigator` method) so it reads from
// `latest` when it is *called*, not when the instance was created. That lets
// us create the autocomplete instance once while `getSources` etc. still see
// the current `customers`, `currentOrganizationId`, `router`, ...
function delegateToLatest(props, latest) {
  return Object.fromEntries(
    Object.entries(props).map(([key, value]) => {
      if (typeof value === 'function') {
        return [key, (...args) => latest.current[key](...args)]
      }

      if (key === 'navigator' && value) {
        return [
          key,
          Object.fromEntries(
            Object.keys(value).map((method) => [
              method,
              (...args) => latest.current.navigator[method](...args),
            ]),
          ),
        ]
      }

      return [key, value]
    }),
  )
}

export default function Autocomplete(props) {
  const containerRef = useRef(null)
  const latestProps = useRef(props)
  latestProps.current = props

  const { t } = useTranslation('translation', {
    useSuspense: false,
    bindI18n: false,
  })

  // Create the instance once. Previously this effect depended on `props`,
  // which is a fresh object every render, so the whole widget (input, open
  // panel, typed query) was destroyed and rebuilt whenever the parent
  // re-rendered. Calling `search.update()` instead would re-run
  // `getSources` (an Algolia request) per render, hence the delegation.
  useEffect(() => {
    if (!containerRef.current) {
      return undefined
    }

    const search = autocomplete({
      detachedMediaQuery: 'none',
      placeholder: t('search.placeholder'),
      container: containerRef.current,
      plugins: [],
      renderer: { createElement, Fragment },
      render({ children }, root) {
        render(children, root)
      },
      ...delegateToLatest(latestProps.current, latestProps),
    })

    return () => {
      search.destroy()
    }
  }, [t])

  return <div className="w-full" ref={containerRef} />
}
